import { ApiError } from '../../api/client';

export type LiveStage = 'listening' | 'stt' | 'nlu' | 'tts' | 'action' | 'done' | string;

export type LiveMessage =
  | { type: 'status'; stage: LiveStage; [key: string]: unknown }
  | { type: 'transcript'; text: string; [key: string]: unknown }
  | { type: 'response'; text: string; audio_url?: string | null; [key: string]: unknown }
  | { type: 'action'; command: unknown; [key: string]: unknown }
  | { type: 'error'; message?: string; code?: string; [key: string]: unknown }
  | { type: 'session_finished'; [key: string]: unknown }
  | { type: string; [key: string]: unknown };

export interface LiveHandlers {
  onOpen?(): void;
  onClose?(code: number): void;
  onMessage?(message: LiveMessage): void;
}

type SocketFactory = (url: string) => WebSocket;

export function readArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

export class LiveSessionClient {
  private socket: WebSocket | null = null;

  constructor(
    private readonly handlers: LiveHandlers,
    private readonly factory: SocketFactory = (url) => new WebSocket(url),
  ) {}

  get connected(): boolean {
    return this.socket?.readyState === 1;
  }

  connect(url: string) {
    this.close();
    const socket = this.factory(url);
    socket.binaryType = 'arraybuffer';
    socket.onopen = () => this.handlers.onOpen?.();
    socket.onclose = (e) => {
      if (this.socket === socket) this.socket = null;
      this.handlers.onClose?.(e.code);
    };
    socket.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      try {
        this.handlers.onMessage?.(JSON.parse(e.data) as LiveMessage);
      } catch {
        this.handlers.onMessage?.({ type: 'error', code: 'invalid_message' });
      }
    };
    this.socket = socket;
  }

  private sendJson(payload: Record<string, unknown>) {
    if (!this.connected) throw new ApiError(0, 'ws_not_connected', 'WebSocket is not connected');
    this.socket?.send(JSON.stringify(payload));
  }

  async sendAudio(blob: Blob, format = 'webm') {
    if (!this.connected) throw new ApiError(0, 'ws_not_connected', 'WebSocket is not connected');
    this.sendJson({ type: 'audio_start', format, mime_type: blob.type || `audio/${format}` });
    this.socket?.send(await readArrayBuffer(blob));
    this.sendJson({ type: 'audio_end', format });
  }

  sendText(text: string) {
    this.sendJson({ type: 'text', text });
  }

  bargeIn() {
    this.sendJson({ type: 'barge_in' });
  }

  finish() {
    this.sendJson({ type: 'finish' });
  }

  close() {
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      s.close();
    }
  }
}
